import React, { useState, useMemo } from 'react';
import {
  Modal, Tabs, Card, Row, Col, Statistic, Table, Tag, Progress, Select, Space, Button, Typography, Empty, Input
} from 'antd';
import {
  LineChartOutlined, TrophyOutlined, TeamOutlined, CalendarOutlined,
  DownloadOutlined, SyncOutlined, ReadOutlined, StarOutlined, SearchOutlined
} from '@ant-design/icons';

const { Text } = Typography;
const { Option } = Select;

const CrossInstitutionComparisonModal = ({
  open,
  onClose,
  data = [],
  loading,
  onRefresh,
  onMediumFilterChange
}) => {
  const [searchText, setSearchText] = useState('');
  const [selectedMediumFilter, setSelectedMediumFilter] = useState('all');
  const [selectedAcademicYearFilter, setSelectedAcademicYearFilter] = useState('all');

  // Collect all unique academic years across all institutions
  const allAcademicYears = useMemo(() => {
    const yearsSet = new Set();
    data.forEach(inst => {
      (inst.years_data || []).forEach(y => {
        if (y.academic_year) yearsSet.add(y.academic_year);
      });
    });
    return Array.from(yearsSet).sort();
  }, [data]);

  // Overall Global Aggregates (Strictly Candidate & Medium metrics, No Gender)
  const globalSummary = useMemo(() => {
    let totalCand = 0;
    let totalScoreSum = 0;
    let totalScoreCount = 0;
    let highestScore = 0;
    let totalPassed = 0;

    data.forEach(inst => {
      const cands = Number(inst.total_candidates_all_years || 0);
      const avg = Number(inst.overall_avg_score || 0);
      const high = Number(inst.all_time_highest_score || 0);
      totalCand += cands;
      if (avg > 0) {
        totalScoreSum += avg * cands;
        totalScoreCount += cands;
      }
      if (high > highestScore) highestScore = high;

      (inst.years_data || []).forEach(y => {
        totalPassed += Number(y.passed_count || 0);
      });
    });

    const globalAvgScore = totalScoreCount > 0 ? (totalScoreSum / totalScoreCount).toFixed(2) : '0.00';
    const globalPassRate = totalCand > 0 ? ((totalPassed / totalCand) * 100).toFixed(1) : '0.0';

    return {
      totalInstitutions: data.length,
      totalCandidates: totalCand,
      globalAvgScore,
      globalPassRate,
      highestScore
    };
  }, [data]);

  // Filtered institutions
  const filteredInstitutions = useMemo(() => {
    return data.filter(inst => {
      const matchesSearch = !searchText || 
        (inst.institution_name && inst.institution_name.toLowerCase().includes(searchText.toLowerCase())) ||
        (inst.institution_head && inst.institution_head.toLowerCase().includes(searchText.toLowerCase()));

      const matchesMedium = selectedMediumFilter === 'all' || 
        (inst.institution_medium && inst.institution_medium.toUpperCase() === selectedMediumFilter.toUpperCase());

      const matchesYear = selectedAcademicYearFilter === 'all' ||
        (inst.years_data || []).some(y => y.academic_year === selectedAcademicYearFilter);

      return matchesSearch && matchesMedium && matchesYear;
    });
  }, [data, searchText, selectedMediumFilter, selectedAcademicYearFilter]);

  // Export Overview to CSV
  const handleExportCSV = () => {
    if (!filteredInstitutions || filteredInstitutions.length === 0) return;

    const headers = [
      'Institution ID',
      'Institution Name',
      'Medium',
      'Total Candidates',
      'Recorded AYs',
      'Overall Avg Score (%)',
      'All-Time Highest (%)',
      'Latest Academic Year',
      'Latest Year Candidates',
      'Latest Year Avg (%)',
      'Latest Year Pass Rate (%)'
    ];

    const rows = filteredInstitutions.map(inst => {
      const latestYear = (inst.years_data && inst.years_data.length > 0) 
        ? inst.years_data[inst.years_data.length - 1] 
        : {};

      return [
        inst.institution_id,
        `"${inst.institution_name || ''}"`,
        `"${inst.institution_medium || 'GENERAL'}"`,
        inst.total_candidates_all_years || 0,
        inst.total_academic_years || 0,
        `${inst.overall_avg_score || 0}%`,
        `${inst.all_time_highest_score || 0}%`,
        `"${latestYear.academic_year || 'N/A'}"`,
        latestYear.total_candidates || 0,
        `${latestYear.avg_percentage || 0}%`,
        `${latestYear.pass_rate || 0}%`
      ];
    });

    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `Institutions_Multi_Year_Candidate_Overview.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const overviewColumns = [
    {
      title: 'Institution',
      key: 'institution_name',
      width: 260,
      render: (_, r) => (
        <div>
          <strong style={{ color: '#6366f1', fontSize: 13 }}>{r.institution_name}</strong>
          <div style={{ marginTop: 3 }}>
            <Tag color={r.institution_medium === 'ENGLISH' ? 'blue' : r.institution_medium === 'KANNADA' ? 'orange' : 'purple'} style={{ fontSize: 10 }}>
              {r.institution_medium || 'GENERAL'}
            </Tag>
            {r.institution_head && (
              <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Head: {r.institution_head}</span>
            )}
          </div>
        </div>
      )
    },
    {
      title: 'Total Candidates',
      dataIndex: 'total_candidates_all_years',
      key: 'total_candidates_all_years',
      width: 140,
      sorter: (a, b) => (a.total_candidates_all_years || 0) - (b.total_candidates_all_years || 0),
      render: (cands, r) => (
        <div>
          <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-color)' }}>
            {(cands || 0).toLocaleString()}
          </span>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
            Across {r.total_academic_years || 0} Years
          </div>
        </div>
      )
    },
    {
      title: 'Overall Avg Score',
      dataIndex: 'overall_avg_score',
      key: 'overall_avg_score',
      width: 150,
      sorter: (a, b) => (a.overall_avg_score || 0) - (b.overall_avg_score || 0),
      render: (avg) => {
        const val = Number(avg || 0);
        return (
          <Tag color={val >= 75 ? 'green' : val >= 60 ? 'gold' : 'blue'} style={{ fontWeight: 700, fontSize: 12 }}>
            {val}%
          </Tag>
        );
      }
    },
    {
      title: 'All-Time Highest',
      dataIndex: 'all_time_highest_score',
      key: 'all_time_highest_score',
      width: 130,
      sorter: (a, b) => (a.all_time_highest_score || 0) - (b.all_time_highest_score || 0),
      render: (score) => (
        <Tag color="magenta" style={{ fontWeight: 700 }}>
          🏆 {score || 0}%
        </Tag>
      )
    },
    {
      title: 'Multi-Year Academic Breakdown',
      key: 'years_breakdown',
      render: (_, r) => {
        const years = r.years_data || [];
        if (years.length === 0) return <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 11 }}>No candidate records</span>;

        return (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {years.map(y => (
              <div 
                key={y.academic_year_id}
                style={{
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid var(--border-color)',
                  borderRadius: 6,
                  padding: '4px 8px',
                  fontSize: 11
                }}
              >
                <strong style={{ color: '#38bdf8' }}>{y.academic_year}:</strong>{' '}
                <span>{y.total_candidates} cands</span> |{' '}
                <span style={{ color: '#10b981' }}>{y.avg_percentage}% avg</span> |{' '}
                <span style={{ color: '#f59e0b' }}>{y.pass_rate}% pass</span>
              </div>
            ))}
          </div>
        );
      }
    }
  ];

  // Top Institutions Rankings
  const topInstitutionsByStrength = useMemo(() => {
    return [...data].sort((a, b) => (b.total_candidates_all_years || 0) - (a.total_candidates_all_years || 0)).slice(0, 5);
  }, [data]);

  const topInstitutionsByScore = useMemo(() => {
    return [...data].filter(d => (d.total_candidates_all_years || 0) > 0).sort((a, b) => (b.overall_avg_score || 0) - (a.overall_avg_score || 0)).slice(0, 5);
  }, [data]);

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: 24, flexWrap: 'wrap', gap: 10 }}>
          <Space size="middle" wrap>
            <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-color)' }}>
              🌐 Multi-Year Candidate Overview (All Institutions)
            </span>
            <Tag color="geekblue">{data.length} Institutions Registered</Tag>
          </Space>
          <Space wrap>
            <Button size="small" icon={<DownloadOutlined />} onClick={handleExportCSV}>
              Export CSV
            </Button>
            <Button size="small" icon={<SyncOutlined spin={loading} />} onClick={onRefresh}>
              Refresh
            </Button>
          </Space>
        </div>
      }
      open={open}
      onCancel={onClose}
      width={1280}
      style={{ top: 16, maxWidth: '96vw' }}
      footer={[
        <Button key="close" type="primary" onClick={onClose}>
          Close
        </Button>
      ]}
      destroyOnClose
      styles={{ body: { maxHeight: '82vh', overflowY: 'auto', padding: '16px 12px' } }}
    >
      {/* 4 Summary Statistic Cards (No Gender) */}
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ background: 'rgba(99, 102, 241, 0.08)', border: '1px solid rgba(99, 102, 241, 0.25)', borderRadius: 10 }}>
            <Statistic
              title={<span style={{ color: 'var(--text-color)', fontSize: 12 }}>Total Enrolled Candidates</span>}
              value={globalSummary.totalCandidates.toLocaleString()}
              valueStyle={{ color: '#6366f1', fontWeight: 'bold', fontSize: 22 }}
              prefix={<TeamOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ background: 'rgba(16, 185, 129, 0.08)', border: '1px solid rgba(16, 185, 129, 0.25)', borderRadius: 10 }}>
            <Statistic
              title={<span style={{ color: 'var(--text-color)', fontSize: 12 }}>System-Wide Avg Score</span>}
              value={`${globalSummary.globalAvgScore}%`}
              valueStyle={{ color: '#10b981', fontWeight: 'bold', fontSize: 22 }}
              prefix={<TrophyOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ background: 'rgba(245, 158, 11, 0.08)', border: '1px solid rgba(245, 158, 11, 0.25)', borderRadius: 10 }}>
            <Statistic
              title={<span style={{ color: 'var(--text-color)', fontSize: 12 }}>System-Wide Pass Rate</span>}
              value={`${globalSummary.globalPassRate}%`}
              valueStyle={{ color: '#f59e0b', fontWeight: 'bold', fontSize: 22 }}
              prefix={<StarOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ background: 'rgba(236, 72, 153, 0.08)', border: '1px solid rgba(236, 72, 153, 0.25)', borderRadius: 10 }}>
            <Statistic
              title={<span style={{ color: 'var(--text-color)', fontSize: 12 }}>Top Score Recorded</span>}
              value={`${globalSummary.highestScore}%`}
              valueStyle={{ color: '#ec4899', fontWeight: 'bold', fontSize: 22 }}
              prefix={<TrophyOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Tabs
        defaultActiveKey="all_institutions"
        items={[
          {
            key: 'all_institutions',
            label: (
              <span>
                <CalendarOutlined style={{ marginRight: 6 }} />
                All Institutions Comparison Matrix ({filteredInstitutions.length})
              </span>
            ),
            children: (
              <div>
                {/* Filter Controls Bar */}
                <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Input
                    placeholder="Search institution name or head..."
                    prefix={<SearchOutlined />}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    style={{ width: 280, borderRadius: 6 }}
                    allowClear
                  />
                  <Select
                    value={selectedMediumFilter}
                    onChange={setSelectedMediumFilter}
                    style={{ width: 170 }}
                  >
                    <Option value="all">All Mediums</Option>
                    <Option value="KANNADA">Kannada Medium</Option>
                    <Option value="ENGLISH">English Medium</Option>
                    <Option value="KANNADA/ENGLISH">Kannada/English</Option>
                  </Select>
                  <Select
                    value={selectedAcademicYearFilter}
                    onChange={setSelectedAcademicYearFilter}
                    style={{ width: 180 }}
                  >
                    <Option value="all">All Academic Years</Option>
                    {allAcademicYears.map(yr => (
                      <Option key={yr} value={yr}>{yr}</Option>
                    ))}
                  </Select>
                  {(searchText || selectedMediumFilter !== 'all' || selectedAcademicYearFilter !== 'all') && (
                    <Button 
                      type="link" 
                      onClick={() => {
                        setSearchText('');
                        setSelectedMediumFilter('all');
                        setSelectedAcademicYearFilter('all');
                      }}
                      style={{ padding: 0 }}
                    >
                      Clear Filters
                    </Button>
                  )}
                </div>

                <Table
                  dataSource={filteredInstitutions}
                  columns={overviewColumns}
                  rowKey="institution_id"
                  size="small"
                  pagination={{ pageSize: 10, showSizeChanger: true }}
                  bordered
                />
              </div>
            )
          },
          {
            key: 'rankings',
            label: (
              <span>
                <TrophyOutlined style={{ marginRight: 6 }} />
                Rankings & Leadership
              </span>
            ),
            children: (
              <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                  <Card 
                    size="small" 
                    title={<span style={{ color: '#6366f1', fontWeight: 700 }}>🏆 Top Institutions by Candidate Strength</span>}
                    style={{ background: 'var(--panel-color)', border: '1px solid var(--border-color)', borderRadius: 8 }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {topInstitutionsByStrength.map((inst, idx) => (
                        <div 
                          key={inst.institution_id}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '8px 12px',
                            background: idx === 0 ? 'rgba(99, 102, 241, 0.1)' : 'rgba(255,255,255,0.02)',
                            borderRadius: 6,
                            borderLeft: idx === 0 ? '3px solid #6366f1' : '1px solid transparent'
                          }}
                        >
                          <div>
                            <strong style={{ fontSize: 13, color: 'var(--text-color)' }}>
                              #{idx + 1} {inst.institution_name}
                            </strong>
                            <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                              <Tag color="geekblue" style={{ fontSize: 9 }}>{inst.institution_medium || 'GENERAL'}</Tag>
                              <span>{inst.total_academic_years} Academic Years</span>
                            </div>
                          </div>
                          <div>
                            <Tag color="purple" style={{ fontWeight: 700, fontSize: 13, padding: '2px 8px' }}>
                              👥 {Number(inst.total_candidates_all_years || 0).toLocaleString()} Candidates
                            </Tag>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card>
                </Col>

                <Col xs={24} md={12}>
                  <Card 
                    size="small" 
                    title={<span style={{ color: '#10b981', fontWeight: 700 }}>⭐ Top Institutions by Academic Average Score</span>}
                    style={{ background: 'var(--panel-color)', border: '1px solid var(--border-color)', borderRadius: 8 }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {topInstitutionsByScore.map((inst, idx) => (
                        <div 
                          key={inst.institution_id}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '8px 12px',
                            background: idx === 0 ? 'rgba(16, 185, 129, 0.1)' : 'rgba(255,255,255,0.02)',
                            borderRadius: 6,
                            borderLeft: idx === 0 ? '3px solid #10b981' : '1px solid transparent'
                          }}
                        >
                          <div>
                            <strong style={{ fontSize: 13, color: 'var(--text-color)' }}>
                              #{idx + 1} {inst.institution_name}
                            </strong>
                            <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                              <Tag color="cyan" style={{ fontSize: 9 }}>{inst.institution_medium || 'GENERAL'}</Tag>
                              <span>{inst.total_candidates_all_years} candidates total</span>
                            </div>
                          </div>
                          <div>
                            <Tag color="green" style={{ fontWeight: 700, fontSize: 13, padding: '2px 8px' }}>
                              🎯 {inst.overall_avg_score}% Avg
                            </Tag>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card>
                </Col>
              </Row>
            )
          }
        ]}
      />
    </Modal>
  );
};

export default CrossInstitutionComparisonModal;
